import { useEffect, useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { SitzungsAktion } from '../session/SitzungsAktion'
import { useSitzung } from '../session/SessionProvider'
import { mitgliedsFarbe } from '../session/logic'
import { verbindeStream } from '../lib/sse'
import { effectsFast, fastSpatial, reducedFade } from '../motion/tokens'
import { Fehlergrenze } from '../ui'
import { useOfflineStatus } from '../lib/offline'
import { MEHR_ZIEL, Navigation } from './Navigation'
import { TopAppBar } from './TopAppBar'
import { Postzaehler } from './Postzaehler'
import { FabProvider } from './FabSlot'
import { sichtbareBereiche } from './bereiche'
import { ladePins, NAVIGATION_GEAENDERT } from './navigation-prefs'

export function AppShell() {
  const { abfrage, caps } = useSitzung()
  const me = abfrage.data
  const ort = useLocation()
  const navigate = useNavigate()
  const reduziert = useReducedMotion()
  const qc = useQueryClient()

  useEffect(() => {
    if (!me || me.setupRequired) return
    return verbindeStream(qc, me.member.id)
  }, [me, qc])

  // Die App-Weiche zeigt die Einrichtung ohne Schale. Der Wächter hält diese
  // Annahme auch für direkte Routen und künftige Umbauten typfest.
  if (!me || me.setupRequired) return null

  const bereiche = useMemo(() => sichtbareBereiche(caps), [caps])
  const [pinIds, setPinIds] = useState<string[]>(() => ladePins(me.member.id, bereiche))

  useEffect(() => {
    const aktualisieren = () => setPinIds(ladePins(me.member.id, bereiche))
    aktualisieren()
    window.addEventListener(NAVIGATION_GEAENDERT, aktualisieren)
    return () => window.removeEventListener(NAVIGATION_GEAENDERT, aktualisieren)
  }, [bereiche, me.member.id])

  useEffect(() => {
    const suchen = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return
      event.preventDefault()
      void navigate('/mehr?suche=1')
    }
    window.addEventListener('keydown', suchen)
    return () => window.removeEventListener('keydown', suchen)
  }, [navigate])

  const heute = bereiche.find((bereich) => bereich.id === 'heute')
  const kompakt = [
    ...(heute ? [heute] : []),
    ...pinIds
      .map((id) => bereiche.find((bereich) => bereich.id === id))
      .filter((bereich) => bereich !== undefined),
    MEHR_ZIEL,
  ]
  const mittel = [
    ...bereiche.filter((bereich) => bereich.id !== 'einstellungen').slice(0, 7),
    MEHR_ZIEL,
  ]
  const { offline, standVon } = useOfflineStatus()
  const farbe = mitgliedsFarbe(me.member.color, me.member.id)

  return (
    <FabProvider>
      <div className="schale">
        <TopAppBar
          haushalt={me.household.name}
          name={me.member.displayName}
          farbe={farbe}
          vorschau={me.preview}
          aktionen={<Postzaehler />}
        >
          <Link className="menue-eintrag" to="/einstellungen" role="menuitem">
            Einstellungen
          </Link>
          <SitzungsAktion />
        </TopAppBar>

        {me.preview ? (
          <p className="vorschau-band" role="status">
            Du siehst Famora gerade als {me.member.displayName}. Änderungen sind dabei gesperrt.
            <SitzungsAktion vorschau />
          </p>
        ) : null}

        {offline ? (
          <p className="offline-band" role="status">
            Offline — Stand von {standVon}
          </p>
        ) : null}

        {/* ⚠️ Der Seitenwechsel blendet NUR auf — kein Abgang, kein Warten.
            Vorher stand hier `AnimatePresence mode="wait"`: die neue Seite
            durfte erst anfangen, wenn die alte fertig abgegangen war. Zusammen
            mit einer ÜBERSCHWINGENDEN Feder auf der Deckkraft (deren langer
            Nachlauf spät „fertig“ meldet) ergab das pro Tipp in der Leiste:
            Abfall ab 165 ms, Deckkraft NULL von 398 bis 800 ms, voll erst bei
            1048 ms — 817 ms blass, davon 0,4 s leere Fläche.

            Zwei Regeln, die schon im Haus stehen, lösen das: was ein- oder
            ausblendet, darf nicht überschwingen (darum `effectsFast` für die
            Deckkraft, `fastSpatial` nur für den Weg) — und ein Abgang lohnt
            nur, wenn darunter etwas liegt, das sichtbar wird. Hier liegt nur
            der Hintergrund. */}
        <motion.main
          key={ort.pathname}
          className="inhalt"
          initial={reduziert ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduziert ? reducedFade : { opacity: effectsFast, y: fastSpatial }}
        >
          {/* ⚠️ Die innere Grenze: eine kaputte Seite lässt Kopfzeile und
              Navigation stehen, man kann weiterklicken. */}
          <Fehlergrenze>
            <Outlet />
          </Fehlergrenze>
        </motion.main>

        <Navigation kompakt={kompakt} mittel={mittel} erweitert={bereiche} />
      </div>
    </FabProvider>
  )
}
